<template>
  <div class="batch-job-detail flex-grow-1 d-flex flex-column overflow-y-hidden">
    <div class="a-header">
      <span class="a-header--title slide-down-enter-active">Detail for Alert {{ incidentDetail.id }}</span>
      <div class="slide-down-enter-active">
        <a-button size="default" class="a-header--button success-btn" @click="rebaselineWebpage"> <img class="mr-2"
            src="@assets/icon/OffHover.svg" /> Re-Baseline </a-button>
        <a-button
          v-if="verifyResult && incidentDetail.webpageId"
          size="default"
          class="a-header--button success-btn"
          @click="openAdjustFinetune"
        >
          <a-icon type="sliders" />Adjust Finetuning
        </a-button>
      </div>
    </div>
    <div class="d-flex justify-content-center align-items-center pt-35-px" v-if="isLoading">
      <Spinner />
    </div>
    <transition name="slide-up">
      <div class="a-body p-20-px flex-grow-1 d-flex flex-column overflow-y-auto" v-if="!isLoading">
        <a-form class="flex-grow-1">
          <div class="info">
            <a-row :gutter="24">
              <a-col :span="24">
                <a-form-item label="Webpage URL">
                  <div class="form-item-readonly">
                    {{ incidentDetail.webpageUrl }}
                  </div>
                </a-form-item>
              </a-col>
              <a-col :span="infoColSpan">
                <a-form-item label="Webpage Name">
                  <a-tooltip :title="incidentDetail.webpageName" placement="topLeft">
                    <div class="form-item-readonly form-item-readonly--ellipsis">
                      {{ incidentDetail.webpageName }}
                    </div>
                  </a-tooltip>
                </a-form-item>
              </a-col>
              <a-col :span="infoColSpan">
                <a-form-item label="Detected">
                  <a-tooltip placement="topLeft">
                    <template slot="title">
                      <time>{{ incidentDetail.detectedAt | dateString }}</time>
                      {{ ' ' }}
                      <time>{{ incidentDetail.detectedAt | timeString }}</time>
                    </template>
                    <div class="form-item-readonly form-item-readonly--ellipsis">
                      <time>{{ incidentDetail.detectedAt | dateString }}</time>
                      {{ ' ' }}
                      <time>{{ incidentDetail.detectedAt | timeString }}</time>
                    </div>
                  </a-tooltip>
                </a-form-item>
              </a-col>
              <a-col :span="infoColSpan">
                <a-form-item label="Alert ID">
                  <a-tooltip :title="String(incidentDetail.id)" placement="topLeft">
                    <div class="form-item-readonly form-item-readonly--ellipsis">
                      {{ incidentDetail.id }}
                    </div>
                  </a-tooltip>
                </a-form-item>
              </a-col>
              <a-col v-if="showHttpStatus" :span="infoColSpan">
                <a-form-item label="HTTP Status">
                  <div class="form-item-readonly">
                    <a-tag :color="httpStatusTagColor">{{ httpStatusLabel }}</a-tag>
                  </div>
                </a-form-item>
              </a-col>
              <a-col :span="24" v-if="triageResult && isAlertTriage">
                <a-form-item>
                  <span slot="label">
                    GenAI Triage
                  </span>
                  <GenAiTriagePanel
                    :severity="triageResult.severity || triageResult.severity_level"
                    :description="triageResult.description"
                    :reasons="triageResult.reasons"
                  />
                </a-form-item>
              </a-col>
              <!-- Shown only when this alert really was aggregated. Most alerts
                   are not, and an empty panel on every alert would be noise. -->
              <a-col :span="24" v-if="aggregationEvent || aggregationSiteIncident">
                <a-form-item label="Aggregated Alert">
                  <AggregatedAlertPanel
                    :event="aggregationEvent"
                    :site-incident="aggregationSiteIncident"
                  />
                </a-form-item>
              </a-col>
              <a-col :span="24" v-if="verifyResult && verifyResult.changes_detected_from_last_incident">
                <a-form-item>
                  <span slot="label">
                    Changes detected (from last alert<template v-if="detectLastIncidentAt"> on <time>{{
                      detectLastIncidentAt | dateString }} {{ detectLastIncidentAt | timeString }}</time></template>)
                  </span>
                  <div class="form-item-readonly change-detect-box">
                    <VerifyResultTable
                      :changes-detected="verifyResult.changes_detected_from_last_incident"
                      :incident-id="incidentDetail.id"
                      comparison-type="previous"
                    />
                  </div>
                </a-form-item>
              </a-col>
              <a-col :span="24" v-if="verifyResult && verifyResult.changes_detected_from_baseline">
                <a-form-item>
                  <span slot="label">
                    Changes detected (from baseline<template v-if="detectBaselineAt"> taken at <time>{{
                      detectBaselineAt
                      | dateString }} {{ detectBaselineAt | timeString }}</time></template>)
                  </span>
                  <div class="form-item-readonly change-detect-box">
                    <template v-if="verifyResult.changes_detected_from_last_incident">
                      <VerifyResultTable
                        :changes-detected="verifyResult.changes_detected_from_baseline"
                        comparison-type="baseline"
                      />
                    </template>
      <template v-else>
                      <!-- we only display links to the incident comparison for baseline if it is not a continuous monitoring incident -->
                      <VerifyResultTable
                        :changes-detected="verifyResult.changes_detected_from_baseline"
                        :incident-id="incidentDetail.id"
                        comparison-type="baseline"
                      />
                    </template>
                  </div>
                </a-form-item>
              </a-col>
              <a-col :span="24" v-if="!verifyResult || verifyResult.network_error">
                <a-form-item label="Change Detected">
                  <div class="form-item-readonly change-detect-box">
                    <div>
                      <div
                        v-for="(line, index) in changesDetectedLines"
                        :key="`change-line-${index}`"
                      >
                        <a
                          v-if="line.href"
                          class="nav-link-detail"
                          :href="line.href"
                          :title="line.title"
                        >
                          {{ line.text }}
                        </a>
                        <span v-else>{{ line.text }}</span>
                      </div>
                    </div>
                  </div>
                </a-form-item>
              </a-col>
              <a-col :span="24">
                <router-link class="button button-secondary d-inline-block mb-20-px"
                  :to="'./' + incidentDetail.id + '/comparison'">
                  <CompareIcon />
                  View Comparison of Differences in HTML, CSS and Javascripts
                </router-link>
              </a-col>
            </a-row>
            <a-row :gutter="24">
              <a-col :span="24">
              </a-col>
            </a-row>
          </div>
          <div>
            <template v-if="!verifyResult && !screenshotImages.incidentImage && incidentDetail.webpageScreenShot">
              <!-- This is the legacy screenshot display, to be used if somehow the new one doesn't work -->
              <div class="mt-30-px mb-10-px">
                <p class="form-label mb-0 d-inline-block">
                  Webpage Screenshot at
                  <time>
                    {{ incidentDetail.detectedAt | dateString }}
                  </time>
                  <time>
                    {{ incidentDetail.detectedAt | timeString }}
                  </time>
                </p>
              </div>
              <div role="button" class="screenshot-container"
                title="Click on screenshot thumbnail to see the full screenshot"
                @click="() => openFullImageInNewTab(incidentDetail.webpageScreenShot)"
                v-if="incidentDetail.webpageScreenShot">
                <ImageItem :source="incidentDetail.webpageScreenShot" />
              </div>
            </template>
            <br>
            <div class="screenshot-image"
              v-if="screenshotImages.baselineImage || screenshotImages.diffImage || screenshotImages.incidentImage">
              <div>
                <span class="screenshot-image__title mr-2">
                  Webpage Screenshot:
                </span>
                <a-radio-group :value="typeImg" @change="handleTypeImageChange">
                  <a-radio-button value="baseline" v-if="screenshotImages.baselineImage">
                    <template v-if="verifyResult && verifyResult.changes_detected_from_last_incident">
                      Last Alert
                    </template>
                    <template v-else>
                      Baseline
                    </template>
                  </a-radio-button>
                  <a-radio-button value="difference" v-if="screenshotImages.diffImage">
                    Difference
                  </a-radio-button>
                  <a-radio-button value="incident" v-if="screenshotImages.incidentImage">
                    Current Alert
                  </a-radio-button>
                </a-radio-group>
                <span class="screenshot-image__title ml-2">
                  <template
                    v-if="(detectBaselineAt && typeImg === 'baseline') || (detectLastIncidentAt && typeImg === 'incident')">
                    at
                    <time v-show="typeImg === 'baseline'">
                      <template v-if="verifyResult && verifyResult.changes_detected_from_last_incident">
                        {{ detectLastIncidentAt | dateString }} {{ detectLastIncidentAt | timeString }}
                      </template>
                      <template v-else>
                        {{ detectBaselineAt | dateString }} {{ detectBaselineAt | timeString }}
                      </template>
                    </time>
                    <time v-show="typeImg === 'incident'">
                      {{ incidentDetail.detectedAt | dateString }} {{ incidentDetail.detectedAt | timeString }}
                    </time>
                  </template>
                </span>
              </div>
              <div class="img-container">
                <img v-show="typeImg === 'baseline'" :src="screenshotImages.baselineImage" alt="Webpage screenshot" />
                <img v-show="typeImg === 'difference'" :src="screenshotImages.diffImage" alt="Webpage screenshot" />
                <img v-show="typeImg === 'incident'" :src="screenshotImages.incidentImage" alt="Webpage screenshot" />
              </div>
            </div>
          </div>
        </a-form>
      </div>
    </transition>
    <a-modal v-model="isShowProgressPerformingBaseline" :footer="null" title="" :closable="false" :maskClosable="false"
      :keyboard="false" class="progress-modal">
      <a-form>
        <a-progress strokeLinecap="square" :percent="percentPerformingBaseline" status="active" />
        <p class="mb-0 text-center font-small">{{ baselineMessage }}</p>
        <p class="mb-0 text-center font-small">Waiting for baseline to complete</p>
        <a-alert showIcon type="warning" class="mt-1">
          <template slot="message">
            Performing intelligent baseline.
            <br />
            This will take about 2 - 3 minutes depending on the contents of the webpage
          </template>
        </a-alert>
      </a-form>
    </a-modal>
    <AdjustFinetuneModal
      :visible="isShowAdjustFinetune"
      :incident-id="incidentDetail.id"
      :webpage-id="incidentDetail.webpageId"
      :webpage-url="incidentDetail.webpageUrl"
      :verify-result="verifyResult"
      @close="isShowAdjustFinetune = false"
    />
  </div>
</template>

<script>
import ImageItem from '@shared/components/ImageItem';
import CompareIcon from '@shared/svg/CompareIcon';
import IncidentService from '../services/Incident.service';
import DataService from '@shared/services/data.service';
import VerifyResultTable from '../../Webpages/components/VerifyResultTable';
import AdjustFinetuneModal from './AdjustFinetuneModal.vue';
import AggregatedAlertPanel from './AggregatedAlertPanel.vue';
import GenAiTriagePanel from './GenAiTriagePanel.vue';
import { formatHttpStatus, getHttpStatusColor } from '@shared/helpers/http-status';

export default {
  name: 'IncidentDetail',
  components: {
    ImageItem,
    CompareIcon,
    VerifyResultTable,
    AdjustFinetuneModal,
    AggregatedAlertPanel,
    GenAiTriagePanel
  },
  data() {
    return {
      incidentDetail: {
        id: '',
        webpageId: '',
        webpageUrl: '',
        webpageName: '',
        detectedAt: '',
        httpStatusCode: null,
        changesDetected: '',
        webpageScreenShot: ''
      },
      changesDetectedLines: [],
      screenshotImages: {
        incidentImage: '',
        baselineImage: '',
        diffImage: '',
      },
      isShowAdjustFinetune: false,
      isShowProgressPerformingBaseline: false,
      percentPerformingBaseline: 0,
      baselineMessage: 'Performing baseline 1 of 3',
      isLoading: true,
      verifyResult: null,
      // What this alert was aggregated into. Both parts stay null for an alert
      // that was never grouped, which is most of them.
      aggregationEvent: null,
      aggregationSiteIncident: null,
      typeImg: 'incident',
    };
  },
  beforeMount() {
    this.incidentDetail.id = this.$route.params.id;
  },
  mounted() {
    this.initData();
  },
  methods: {
    openAdjustFinetune() {
      this.isShowAdjustFinetune = true;
    },
    buildChangesDetectedLines(keysToReplace = []) {
      const sourceText = String(this.incidentDetail.changesDetected || '');
      if (!sourceText) {
        return [];
      }
      const keys = (Array.isArray(keysToReplace) ? keysToReplace : []).filter(Boolean);
      return sourceText.split(/\n/gim).map((lineText) => {
        for (let index = 0; index < keys.length; index++) {
          const keyToReplace = keys[index];
          if (lineText.includes(keyToReplace)) {
            return {
              text: lineText,
              href: `${this.$route.path}/comparison#${keyToReplace}`,
              title: `Compare ${keyToReplace}`,
            };
          }
        }
        return { text: lineText };
      });
    },
    async initData() {
      await this.fetchDetail();
      await this.fetchFilesToCompare();
      await this.fetchAggregation();
    },
    /**
     * Asks what this alert was grouped into. One request, and only on this
     * screen: there is no batch form of this endpoint, so an alert list must
     * never call it per row.
     *
     * A failure is silent. This panel is extra context, and an account without
     * the feature is answered with 403, which is not an error worth a message.
     */
    async fetchAggregation() {
      if (!this.hasChangeEvents) return;
      try {
        const result = await IncidentService.fetchAggregation(this.incidentDetail.id);
        const body = (result && result.data) || result || {};
        this.aggregationEvent = body.change_event || null;
        this.aggregationSiteIncident = body.site_incident || null;
      } catch {
        this.aggregationEvent = null;
        this.aggregationSiteIncident = null;
      }
    },
    async fetchDetail() {

      let url, time_detected, verify_record = null;
      try {
        const incidentDetail = await IncidentService.fetchDetailIncident(this.incidentDetail.id);
        console.log(incidentDetail);
        url = incidentDetail.url;
        time_detected = incidentDetail.time_detected;
        verify_record = incidentDetail.verify_record;
        console.log(verify_record);
        this.incidentDetail.webpageId = url && url.id;
        this.incidentDetail.webpageUrl = url && url.url;
        this.incidentDetail.webpageName = url && url.name;
        this.incidentDetail.detectedAt = time_detected;
        this.incidentDetail.httpStatusCode = incidentDetail.http_status_code;
        this.incidentDetail.changesDetected = (verify_record && verify_record.comment) || '';

        // Handle case where verify record is missing
        if (verify_record === null) {
          // We use the incident comment instead
          this.incidentDetail.changesDetected = incidentDetail.comment;
        }
        this.changesDetectedLines = this.buildChangesDetectedLines();
      } catch {
        this.$showErrorMessage('Error when loading detail incident');
      } finally {
        if (this.isLoading) {
          setTimeout(() => {
            this.isLoading = false;
          }, 3000);
        }
      }

      try {
        const verifyResult = (await IncidentService.fetchDetailVerify(verify_record.id));
        this.verifyResult = verifyResult ? verifyResult.structured_result : null;
        this.screenshotImages.incidentImage = (this.verifyResult.screenshot && this.verifyResult.screenshot.after) || '';
        // Note: baselineImage is the "before" image from the API, which can either be the baseline or last alert
        this.screenshotImages.baselineImage = (this.verifyResult.screenshot && this.verifyResult.screenshot.before) || '';
        this.screenshotImages.diffImage = (this.verifyResult.screenshot && this.verifyResult.screenshot.difference) || '';

        this.incidentTableData = {
          contentAnalyticsData: this.contentAnalyticsDataIncident,
          integrityAnalyticsData: this.integrityAnalyticsDataIncident,
          imgAnalyticsData: this.imgAnalyticsDataIncident,
          staticMalwareScanData: this.staticMalwareScanDataVerify,
          keywordSetsData: this.keywordSetsDataFromLastIncident
        }

        this.baselineTableData = {
          contentAnalyticsData: this.contentAnalyticsDataBaseline,
          integrityAnalyticsData: this.integrityAnalyticsDataBaseline,
          imgAnalyticsData: this.imgAnalyticsDataBaseline,
          staticMalwareScanData: this.staticMalwareScanDataVerify,
          keywordSetsData: this.keywordSetsDataFromBaseline
        }
      } catch {
        // If we don't have the verify detail (e.g. webpage unreachable or old entry)
        // We will show the old text-based UI

        // We still try see if there are screenshots (even if we don't have the verify detail data)
        // by calling the incident screenshots API
        try {
          const screenshots = (await IncidentService.fetchScreenShot(this.incidentDetail.id));

          this.screenshotImages.baselineImage = (screenshots.baseline) || '';
          this.screenshotImages.diffImage = (screenshots.incident_difference_with_baseline) || '';
          //this.screenshotImages.previousIncidentImage = (screenshots.previous_incident) || '';
          //this.screenshotImages.previousIncidentDiffImage = (screenshots.incident_difference_with_previous_incident) || '';
          this.screenshotImages.incidentImage = (screenshots.incident) || '';
        } catch {
          // If the above also fails, we use the oldest screenshot method, which is just the incident screenshot
          try {
            await this.fetchIncidentScreenShot(this.incidentDetail.id);
          } catch {
            //this.$showErrorMessage('Unable to fetch screenshot');
          }
        }
      } finally {
        if (this.isLoading) {
          setTimeout(() => {
            this.isLoading = false;
          }, 3000);
        }
      }

    },
    async fetchFilesToCompare() {
      try {
        const filesResponse = await IncidentService.fetchFilesToCompare(this.incidentDetail.id);
        const { js, css } = filesResponse;
        const cssKeys = css ? Object.keys(css) : [];
        const jsKeys = js ? Object.keys(js) : [];
        const keysToReplace = [...cssKeys, ...jsKeys];
        this.changesDetectedLines = this.buildChangesDetectedLines(keysToReplace);
      } catch {
        this.$showErrorMessage('Error when loading files');
      }
    },
    async fetchIncidentScreenShot(incId) {
      try {
        const incidentImages = await IncidentService.fetchScreenShot(incId);
        this.incidentDetail.webpageScreenShot = incidentImages.incident;
      } catch {
        this.incidentDetail.webpageScreenShot = '';
        this.$showErrorMessage('Error when loading incident screenshot');
      }
    },
    rebaselineWebpage() {
      this.$confirm({
        title: 'You will be redirected to another page. Please save your configurations before proceeding.',
        okText: 'Yes',
        okType: 'danger',
        cancelText: 'No',
        onOk: async () => {
          this.isShowProgressPerformingBaseline = true;
          const performaneReBaselineResponse = await IncidentService.reBaselineWebpage(this.incidentDetail.webpageId);
          const { job_id } = performaneReBaselineResponse;
          const recursionPerformingBaselineRequest = async () => {
            try {
              const verifyResponse = await DataService.fetchStatusJob(job_id);
              const { status, result } = verifyResponse;
              if (status === 'Completed') {
                this.isShowProgressPerformingBaseline = false;
                this.$showSuccessMessage('The baseline of your webpage is successful!');
                this.$router.push(`/webpages/baseline-result/${this.incidentDetail.webpageId}`);
              } else {
                this.baselineMessage = result ? JSON.parse(result).message : this.baselineMessage;
                const percentPerforming = result ? Math.min(JSON.parse(result).start_progress, 100) : 0;
                this.percentPerformingBaseline = +percentPerforming.toFixed();
                setTimeout(() => {
                  recursionPerformingBaselineRequest();
                }, 3000);
              }
            } catch {
              this.isShowProgressPerformingBaseline = false;
              this.$showErrorMessage('The baseline of your webpage is error.');
            }
          };
          recursionPerformingBaselineRequest();
        }
      });
    },
    openFullImageInNewTab(imageData) {
      const image = new Image();
      image.src = imageData;
      image.style.display = 'block';
      image.style.margin = 'auto';
      const w = window.open('');
      w.document.write(image.outerHTML);
      w.document.title = `Webpage screenshot - ${this.incidentDetail.webpageUrl}`;
    },
    handleTypeImageChange(e) {
      this.typeImg = e.target.value;
    },
  },
  computed: {
    triageResult() {
      const triageResult = this.verifyResult && this.verifyResult.triage_result;
      if (Array.isArray(triageResult)) {
        return triageResult[0] || null;
      }
      return triageResult || null;
    },
    featureAccess() {
      return this.$store.state.user.data.feature_access;
    },
    isAlertTriage() {
      return Boolean(this.featureAccess['alert-triage']);
    },
    hasChangeEvents() {
      return Boolean(this.featureAccess && this.featureAccess['change-events']);
    },
    isStatusCodeMonitoring() {
      return this.$store.getters.hasFeature('status-code-monitoring');
    },
    showHttpStatus() {
      return this.isStatusCodeMonitoring && this.httpStatusLabel !== null;
    },
    // Four fields share one row when HTTP Status is on (6+6+6+6).
    // Three fields keep the current layout when it is off (8+8+8).
    infoColSpan() {
      return this.showHttpStatus ? 6 : 8;
    },
    httpStatusCode() {
      const fromIncident = this.incidentDetail.httpStatusCode;
      if (fromIncident !== undefined && fromIncident !== null && fromIncident !== '') {
        return fromIncident;
      }
      return this.verifyResult && this.verifyResult.http_status_code;
    },
    httpStatusLabel() {
      return formatHttpStatus(this.httpStatusCode);
    },
    httpStatusTagColor() {
      return getHttpStatusColor(this.httpStatusCode);
    },
    detectLastIncidentAt() {
      return this.verifyResult && this.verifyResult.last_incident && this.verifyResult.last_incident.detected_time;
    },
    detectBaselineAt() {
      return this.verifyResult && this.verifyResult.baseline && this.verifyResult.baseline.start_time;
    }
  }
};
</script>

<style lang="scss" scoped>
.a-header--button {
  padding: 6px 20px;
  border-radius: 4px;
  font-size: 11px;
  line-height: 16px;

  &.success-btn {
    background-color: #04b0ed;
    color: #ffffff;
  }
}

.a-body {
  height: calc(100% - 60px);

  .info {
    background: #f7f7f7;
    padding: 0 20px;
    border-radius: 4px;
  }

  .a-filter {
    padding-left: 0;
    padding-right: 0;
  }

  .a-table-data {
    flex-grow: 1;
    margin-left: -20px;
    margin-right: -20px;
  }

  .change-detect-box {
    div:first-child {
      line-height: 25px;
      padding: 5px 0px;
      white-space: pre-wrap;
    }
  }

}

.form-item-readonly {
  word-break: break-word;
  font-size: 13px;
  padding: 0 11px;
  height: auto;
  min-height: 40px;
  color: rgba(0, 0, 0, 0.65);
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background-color: transparent;
}

.form-item-readonly--ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 40px;
}

.nav-link-detail {
  text-decoration: underline;
}

</style>
